import React, { useCallback, useEffect, useState } from 'react';
import { Topbar, CalendarModal } from '@components';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
    Radio,
    Spin,
} from 'antd';
import {
    CalendarFilled,
    WithdrawTwoTone,
    TopupTwoTone,
    BonusTwoTone,
} from '@assets';
import { PRIVATE_ROUTE } from '@router/route.constants';
import { api } from '@api';
import {
    LIMITS,
    TOPUP_SK,
    WITHDRAW_SK,
    BONUS_SK,
} from '@constants';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useTranslation } from 'react-i18next';
import {
    EmptyText,
    ListContainer,
} from '../Wallet/Wallet.style';
import {
    ContainerOutlined,
} from '@ant-design/icons';
import TransactionListItem from '../Wallet/TransactionListItem/TransactionListItem';

const RootContainer = styled.div`
padding-bottom: 16px;
height: calc(100vh - 86px);
overflow: hidden;

.empty-box {
    height: calc(100vh - 115px);
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: center;
    justify-content: center;

    svg {
        font-size: 24px;
        color: #808080;
    }
}

.anticon .anticon-calendar {
    font-size: 28px;
    color: #fff;
}
`;

const RadioGroup = styled(Radio.Group)`
  display: flex;
  align-items: center;
  justify-content: space-around;

  .ant-radio-wrapper span.ant-radio + * {
    padding-inline-start: 0px !important;
    padding-inline-end: 0 !important;
  }

  @media screen and (max-width: 355px) {
    .ant-radio-wrapper span.ant-radio + * {
        padding-inline-end: 0 !important;
    }
  }
`;

const RadioButton = styled(Radio)`
  .ant-radio {
    opacity: 0;
    position: fixed;
    width: 0;
  }
`;

const Text = styled.div`
  padding: 12px;
  min-width: 50px;
  display: flex;
  gap: 3px;
  justify-content: center;
  align-items: center;
  
  font-size: 12px;
  font-weight: 500;
  color: ${(props) =>
        props.status
            ? "#49028E"
            : "#9B92BB"};
  border-bottom: ${(props) =>
        props.status
            ? "1px solid #9027E1"
            : "none"};

  @media screen and (max-width: 411px) {
    min-width: 30px;
    padding: 12px 6px;
    gap: 2px;
  }

  @media screen and (max-width: 355px) {
    padding: 12px 4px;
    font-size: 11px;
  }
`;

const StyledListWrapper = styled(ListContainer).attrs({
    id: 'scrollable-div',
})`
height: calc(100vh - 114px);
`;

const SpinWrapper = styled.div`
padding-top: 12px;
text-align: center;
`;

const {
    DEFAULT_PAGE,
    PAGE_SIZE,
} = LIMITS;

const apiDateFormat = 'YYYY-MM-DD';

const TransactionHistory = () => {
    const { t } = useTranslation()
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [type, setType] = useState('');
    const [page, setPage] = useState(DEFAULT_PAGE);
    const [total, setTotal] = useState(0);
    const [open, setOpen] = useState(false);
    const [date, setDate] = useState('');
    const [loading, setLoading] = useState(true);

    const getTransactions = useCallback(() => {
        setLoading(true);
        api
            .getTransactions({
                page,
                limit: PAGE_SIZE,
                type,
                start_date: date,
                end_date: date,
            })
            .then(({ transactions, pagination }) => {
                setData(prevState => {
                    return [
                        ...prevState,
                        ...transactions,
                    ];
                });
                setPage(pagination.page);
                setTotal(pagination.total);
                setLoading(false);
            })
            .catch(() => { });

    }, [type, page, date]);

    useEffect(() => {
        getTransactions();

    }, [getTransactions]);

    const onChange = (e) => {
        setType(e.target.value);
        setPage(DEFAULT_PAGE);
        setData([]);
        setTotal(0);
    };

    const onOk = (newdate) => {
        newdate = newdate.format(apiDateFormat);
        if (newdate !== date) {
            setDate(newdate);
            setType('');
            setPage(DEFAULT_PAGE);
            setData([]);
            setTotal(0);
        }
        handleClose();
    }

    const onReset = () => {
        if (date) {
            setDate('');
            setType('');
            setPage(DEFAULT_PAGE);
            setData([]);
            setTotal(0);
        }
    }

    const handleClose = () => {
        setOpen(false);
    }

    const goBack = () => {
        navigate(`/${PRIVATE_ROUTE.WALLET}`);
    }

    const renderTop = () => (
        <RadioGroup onChange={onChange} value={type}>
            <RadioButton value={""}>
                <Text status={type === ""}>
                    {t("wallet.transactionHistoryHeadingAll")}
                </Text>
            </RadioButton>
            <RadioButton value={WITHDRAW_SK}>
                <Text status={type === WITHDRAW_SK}>
                    <WithdrawTwoTone />
                    {t("wallet.walletWithdraw")}
                </Text>
            </RadioButton>
            <RadioButton value={TOPUP_SK}>
                <Text status={type === TOPUP_SK}>
                    <TopupTwoTone />
                    {t("wallet.walletTopUp")}
                </Text>
            </RadioButton>
            <RadioButton value={BONUS_SK}>
                <Text status={type === BONUS_SK}>
                    <BonusTwoTone />
                    {t("wallet.transactionHistoryHeadingBonus")}
                </Text>
            </RadioButton>
        </RadioGroup>
    );

    const Loader = () => (
        <SpinWrapper>
            <Spin />
        </SpinWrapper>
    );

    const EmptyCard = () => (
        <div className="empty-box">
            <ContainerOutlined />
            <EmptyText>
                {t("common.noRecord")}
            </EmptyText>
        </div>
    );

    return (
        <>
            <Topbar
                title={t("wallet.transactionHistoryTitle")}
                rightIcon={
                    <CalendarFilled onClick={() => setOpen(true)} />
                }
                goBack={goBack}
            />
            <RootContainer>
                {renderTop()}
                <StyledListWrapper>
                    <InfiniteScroll
                        dataLength={data.length}
                        next={() => setPage((prev) => prev + 1)}
                        hasMore={loading || data.length < total}
                        loader={<Loader />}
                        scrollableTarget='scrollable-div'
                        endMessage={!loading && data.length === 0 && <EmptyCard />}
                    >
                        {data.map((item, key) => (
                            <TransactionListItem key={key} item={item} />
                        ))}
                    </InfiniteScroll>
                </StyledListWrapper>
            </RootContainer>
            <CalendarModal
                open={open}
                date={date}
                onOk={onOk}
                onReset={onReset}
                handleClose={handleClose}
            />
        </>
    );
}

export default TransactionHistory;